import {
  Typography,
  TextField,
  Button,
  Stack,
  Box,
  Dialog,
  Checkbox,
  InputAdornment,
  Tooltip,
  Alert,
  MenuItem,
  Chip,
  IconButton,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import Iconify from '../../../../components/iconify/Iconify';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { ProductService } from '../../../../api/product.service';
import { GoodsInService, GoodsInLot } from '../../../../api/goods-in.service';
import { ProductType } from '../../../../@types/product';
import { useFormContext, Controller, useWatch } from 'react-hook-form';

const differentRLCheckboxProps = {
  inputProps: { 'aria-label': 'Different right and left contact lens' },
};

/** Order of sections in the picker (matches inventory ProductType). */
const DISPENSE_PRODUCT_TYPE_ORDER: ProductType[] = [
  ProductType.FRAMES,
  ProductType.SUNGLASSES,
  ProductType.LENSES,
  ProductType.CONTACT_LENSES,
  ProductType.ACCESSORIES,
  ProductType.SUNDRIES,
  ProductType.SOLUTION,
];

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  [ProductType.FRAMES]: 'Frames',
  [ProductType.SUNGLASSES]: 'Sunglasses',
  [ProductType.LENSES]: 'Lenses (spectacle)',
  [ProductType.CONTACT_LENSES]: 'Contact lenses',
  [ProductType.ACCESSORIES]: 'Accessories',
  [ProductType.SUNDRIES]: 'Sundries',
  [ProductType.SOLUTION]: 'Solution',
  other: 'Other / uncategorized',
};

/**
 * Maps Choose Specs Type → product.category for spectacle lenses only.
 * If no products in the catalog use that category, we do not restrict (avoid empty picker).
 */
const SPEC_TYPE_TO_LENS_CATEGORY: Partial<Record<string, string>> = {
  Bifocal: 'bifocal',
  Varifocal: 'progressive',
  Distance: 'single_vision',
  Near: 'single_vision',
  Intermediate: 'single_vision',
};

const productPickerColumns: GridColDef<Row>[] = [
  { field: 'name', headerName: 'Name', minWidth: 140, flex: 0.55 },
  { field: 'code', headerName: 'Code', minWidth: 110, flex: 0.32 },
  { field: 'sku', headerName: 'SKU', minWidth: 110, flex: 0.32 },
  { field: 'brand', headerName: 'Brand', minWidth: 110, flex: 0.32 },
  { field: 'color', headerName: 'Color', minWidth: 100, flex: 0.28 },
  { field: 'model', headerName: 'Model', minWidth: 120, flex: 0.32 },
  { field: 'material', headerName: 'Material', minWidth: 120, flex: 0.32 },
  { field: 'size', headerName: 'Size', minWidth: 100, flex: 0.25 },
  { field: 'type', headerName: 'Type', minWidth: 110, flex: 0.35 },
  { field: 'category', headerName: 'Category', minWidth: 120, flex: 0.35 },
  {
    field: 'currentStock',
    headerName: 'Stock',
    type: 'number',
    minWidth: 80,
    flex: 0.22,
  },
  {
    field: 'salePrice',
    headerName: 'Price',
    type: 'number',
    minWidth: 90,
    flex: 0.28,
  },
  { field: 'status', headerName: 'Status', minWidth: 100, flex: 0.28 },
];

type Row = {
  id: string | number;
  name: string;
  code: string;
  sku: string;
  brand: string;
  color: string;
  model: string;
  material: string;
  size: string;
  type?: string;
  salePrice: number | string;
  status: string;
  category?: string;
  currentStock: number;
  requiresLotTracking?: boolean;
  packSize?: number;
};

function isBatchAwareProduct(
  value: Record<string, unknown> | Row | undefined,
): boolean {
  if (!value) return false;
  if (value.requiresLotTracking === true) return true;
  const type = value.type;
  return type === ProductType.CONTACT_LENSES || type === ProductType.SOLUTION;
}

/** Units on a basket line (lot-tracked lines count issued packs). */
function lineQuantity(line: Record<string, unknown> | undefined): number {
  if (!line) return 1;
  const raw = isBatchAwareProduct(line)
    ? (line.quantityIssued ?? line.quantity)
    : line.quantity;
  return Math.max(1, Math.floor(Number(raw ?? 1)) || 1);
}

function formatGbp(value: unknown): string {
  const n = Number(value);
  return `£${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
}

/** A fresh basket line for a catalogue product. */
function lineFromProduct(item: Row) {
  return {
    id: item.id,
    name: item.name,
    type: item.type,
    price: item.salePrice,
    category: item.category ?? item.type ?? '',
    requiresLotTracking: Boolean(item.requiresLotTracking),
    packSize:
      item.packSize != null ? Math.max(1, Number(item.packSize) || 1) : 1,
    goodsInId: '',
    batchNumber: '',
    quantity: 1,
    quantityIssued: 1,
  };
}

function filterRowsBySearch(allProducts: Row[], filterSearch: string): Row[] {
  const q = filterSearch.trim().toLowerCase();
  if (!q) return allProducts;
  return allProducts.filter((row) => {
    const haystack = [
      row.name,
      row.code,
      row.sku,
      row.brand,
      row.model,
      row.material,
      row.color,
      row.type,
      row.category,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}

function groupRowsByProductType(rows: Row[]): Record<string, Row[]> {
  const g: Record<string, Row[]> = { other: [] };
  for (const pt of DISPENSE_PRODUCT_TYPE_ORDER) {
    g[pt] = [];
  }
  for (const row of rows) {
    const t = row.type;
    if (t && DISPENSE_PRODUCT_TYPE_ORDER.includes(t as ProductType)) {
      g[t].push(row);
    } else {
      g.other.push(row);
    }
  }
  return g;
}

/** First accordion that has products in the full catalog (used when dialog opens). */
function getFirstPanelWithRows(catalog: Row[]): string | false {
  const grouped = groupRowsByProductType(catalog);
  const first = DISPENSE_PRODUCT_TYPE_ORDER.find(
    (pt) => (grouped[pt]?.length ?? 0) > 0,
  );
  if (first) return first;
  if (grouped.other.length > 0) return 'other';
  return ProductType.FRAMES;
}

/** Matches DispensePage — general retail; all inventory types selectable. */
const DISPENSE_OTHER_PRODUCTS = 'dispense_other_products';

function isSelectableForDispenseMode(row: Row, dispenseMode: string): boolean {
  if (!dispenseMode) return true;
  if (dispenseMode === DISPENSE_OTHER_PRODUCTS) return true;
  if (dispenseMode === 'dispense_frames') {
    return row.type !== ProductType.CONTACT_LENSES;
  }
  if (dispenseMode === 'dispense_contact_lenses') {
    return row.type === ProductType.CONTACT_LENSES;
  }
  return true;
}

function isSelectableForSpecType(
  row: Row,
  specType: string,
  catalog: Row[],
): boolean {
  if (!specType) return true;
  if (row.type !== ProductType.LENSES) return true;

  const wanted = SPEC_TYPE_TO_LENS_CATEGORY[specType];
  if (!wanted) return true;

  const spectacleLenses = catalog.filter((r) => r.type === ProductType.LENSES);
  const hasAnyInCategory = spectacleLenses.some((r) => r.category === wanted);
  if (!hasAnyInCategory) return true;

  return row.category === wanted;
}

function isRowSelectable(
  row: Row,
  dispenseMode: string,
  specType: string,
  catalog: Row[],
): boolean {
  return (
    isSelectableForDispenseMode(row, dispenseMode) &&
    isSelectableForSpecType(row, specType, catalog)
  );
}

interface PageProps {
  specType: string;
  dispenseMode: string;
  fields: any[];
  append: Function;
  remove: Function;
  disabled: boolean;
}

export default function FramesAndLenses({
  specType,
  dispenseMode,
  fields,
  append,
  remove,
  disabled,
}: PageProps) {
  const { control, setValue } = useFormContext();
  const linesWatched = useWatch({ control, name: 'framesAndLenses' }) as
    Record<string, unknown>[] | undefined;
  const [lotsByProductId, setLotsByProductId] = useState<
    Record<string, GoodsInLot[]>
  >({});
  const loadedLotProductIdsRef = useRef(new Set<string>());

  const ensureLotsLoaded = useCallback(async (productId: string) => {
    if (!productId || loadedLotProductIdsRef.current.has(productId)) return;
    loadedLotProductIdsRef.current.add(productId);
    try {
      const { data } = await new GoodsInService().getAvailableLots(productId);
      setLotsByProductId((prev) => ({
        ...prev,
        [productId]: Array.isArray(data) ? data : [],
      }));
    } catch {
      setLotsByProductId((prev) => ({ ...prev, [productId]: [] }));
    }
  }, []);
  const [formModal, setFormModal] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [expandedType, setExpandedType] = useState<string | false>(
    ProductType.FRAMES,
  );
  const [differentRL, setDifferentRL] = useState(false);
  const [allProducts, setAllProducts] = useState<Row[]>([]);
  const [chooseFieldIndex, setChooseFieldIndex] = useState<number | null>(null);

  const displayProducts = useMemo(
    () => filterRowsBySearch(allProducts, filterSearch),
    [allProducts, filterSearch],
  );

  const groupedByType = useMemo(
    () => groupRowsByProductType(displayProducts),
    [displayProducts],
  );

  const wasModalOpenRef = useRef(false);

  useEffect(() => {
    const opening = formModal && !wasModalOpenRef.current;
    wasModalOpenRef.current = formModal;

    if (opening) {
      setExpandedType(getFirstPanelWithRows(allProducts));
    }
  }, [formModal, allProducts]);

  const fetchActiveProducts = async () => {
    const productService = new ProductService();
    try {
      const { data } = await productService.getActiveProducts();

      const mapped: Row[] = data.map((product: any) => {
        const pd = product.productDetails ?? {};
        return {
          id: product.id,
          name: product.name,
          code: product.code,
          sku: product.sku ?? '',
          brand: product.brand?.name ?? '',
          color: pd.color ?? '',
          model: pd.model ?? '',
          material: pd.material ?? '',
          size: pd.size ?? '',
          type: product.type,
          salePrice: product.salePrice ?? '',
          status: product.status ?? '',
          category: product.category,
          currentStock: Number(product.currentStock ?? 0),
          requiresLotTracking: Boolean(product.requiresLotTracking),
          packSize:
            product.packSize != null ? Number(product.packSize) : undefined,
        };
      });

      setAllProducts(mapped);
      setFilterSearch('');
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    fetchActiveProducts();
  }, []);

  useEffect(() => {
    if (!linesWatched?.length) {
      loadedLotProductIdsRef.current.clear();
      return;
    }
    for (const row of linesWatched as Record<string, unknown>[]) {
      const pid = row?.id;
      if (!pid) continue;
      if (isBatchAwareProduct(row)) {
        void ensureLotsLoaded(String(pid));
      }
    }
  }, [linesWatched, ensureLotsLoaded]);

  const handleAccordionChange =
    (panel: string) => (_: unknown, isExpanded: boolean) => {
      setExpandedType(isExpanded ? panel : false);
    };

  const handleCloseModal = () => {
    setFormModal(false);
    setDifferentRL(false);
  };

  const { enqueueSnackbar } = useSnackbar();

  /** Units of each product already in the basket (shown in the picker). */
  const basketQtyByProduct = useMemo(() => {
    const qtyMap = new Map<string, number>();
    (linesWatched ?? []).forEach((line) => {
      const pid = line?.id ? String(line.id) : '';
      if (!pid) return;
      qtyMap.set(pid, (qtyMap.get(pid) ?? 0) + lineQuantity(line));
    });
    return qtyMap;
  }, [linesWatched]);

  const stockByProduct = useMemo(
    () => new Map(allProducts.map((p) => [String(p.id), p.currentStock])),
    [allProducts],
  );

  const openPicker = useCallback((index: number | null) => {
    setChooseFieldIndex(index);
    setFilterSearch('');
    setFormModal(true);
  }, []);

  /** "Add Product" opens the catalogue straight away — no empty row first. */
  const handleAddFrame = () => openPicker(null);

  const setLineQuantity = useCallback(
    (index: number, qty: number) => {
      const next = Math.max(1, Math.floor(qty) || 1);
      setValue(`framesAndLenses.${index}.quantity`, next, {
        shouldDirty: true,
      });
      setValue(`framesAndLenses.${index}.quantityIssued`, next, {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  /** Index of the plain (non lot-tracked) basket line for a product, or -1. */
  const findPlainLineIndex = (productId: string, skipIndex?: number) =>
    (linesWatched ?? []).findIndex(
      (line, i) =>
        i !== skipIndex &&
        !!line?.id &&
        String(line.id) === productId &&
        !isBatchAwareProduct(line),
    );

  const handlePickProduct = (selectedItem: Row) => {
    if (!isRowSelectable(selectedItem, dispenseMode, specType, allProducts)) {
      return;
    }
    const pid = String(selectedItem.id);
    const plain = !isBatchAwareProduct(selectedItem);

    // "Change" on an existing line: swap the product in place, or fold the
    // line into an existing line for the same product.
    if (chooseFieldIndex !== null) {
      const sameIndex = plain ? findPlainLineIndex(pid, chooseFieldIndex) : -1;
      if (sameIndex !== -1) {
        const moved = lineQuantity(linesWatched?.[chooseFieldIndex]);
        setLineQuantity(
          sameIndex,
          lineQuantity(linesWatched?.[sameIndex]) + moved,
        );
        remove(chooseFieldIndex);
      } else {
        Object.entries(lineFromProduct(selectedItem)).forEach(
          ([key, value]) => {
            setValue(`framesAndLenses.${chooseFieldIndex}.${key}`, value, {
              shouldDirty: true,
            });
          },
        );
      }
      if (!plain) void ensureLotsLoaded(pid);
      handleCloseModal();
      return;
    }

    // Adding: the same product again bumps the quantity instead of a new row.
    const existingIndex = plain ? findPlainLineIndex(pid) : -1;
    if (existingIndex !== -1) {
      const next = lineQuantity(linesWatched?.[existingIndex]) + 1;
      setLineQuantity(existingIndex, next);
      enqueueSnackbar(`${selectedItem.name} × ${next} in basket`, {
        variant: 'success',
      });
      return;
    }
    append(lineFromProduct(selectedItem));
    if (!plain) void ensureLotsLoaded(pid);
    enqueueSnackbar(`${selectedItem.name} added to basket`, {
      variant: 'success',
    });
  };

  // DataGrid columns are memoised; the latest handler is read through a ref.
  const pickRef = useRef(handlePickProduct);
  pickRef.current = handlePickProduct;

  const isReplacing = chooseFieldIndex !== null;

  const pickerColumns = useMemo<GridColDef<Row>[]>(
    () => [
      {
        field: '__basket',
        headerName: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: 170,
        flex: 0.45,
        renderCell: (params) => {
          const selectable = isRowSelectable(
            params.row,
            dispenseMode,
            specType,
            allProducts,
          );
          const inBasket = basketQtyByProduct.get(String(params.row.id)) ?? 0;
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant={inBasket ? 'outlined' : 'contained'}
                disabled={!selectable}
                startIcon={
                  <Iconify
                    icon={
                      isReplacing
                        ? 'eva:swap-fill'
                        : 'eva:shopping-cart-outline'
                    }
                  />
                }
                onClick={(event) => {
                  event.stopPropagation();
                  pickRef.current(params.row);
                }}
              >
                {isReplacing ? 'Select' : inBasket ? 'Add another' : 'Add'}
              </Button>
              {inBasket > 0 && (
                <Chip size="small" color="success" label={`× ${inBasket}`} />
              )}
            </Stack>
          );
        },
      },
      ...productPickerColumns,
    ],
    [basketQtyByProduct, isReplacing, dispenseMode, specType, allProducts],
  );

  const gridSx = {
    '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
      cursor: 'default',
    },
    '& .MuiDataGrid-row:not(.dispense-product-row--disabled)': {
      cursor: 'pointer',
    },
    '& .MuiDataGrid-row:not(.dispense-product-row--disabled) .MuiDataGrid-cell':
      {
        cursor: 'pointer',
        userSelect: 'none',
      },
    '& .dispense-product-row--disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
    '& .MuiDataGrid-row.dispense-product-row--disabled .MuiDataGrid-cell': {
      cursor: 'not-allowed',
      userSelect: 'none',
    },
  };

  const renderTypeGrid = (rows: Row[], key: string) => (
    <div style={{ height: 360, width: '100%' }} key={key}>
      <DataGrid
        rows={rows}
        columns={pickerColumns}
        getRowId={(row) => String(row.id)}
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams<Row>) =>
          handlePickProduct(params.row)
        }
        pageSizeOptions={[5, 10, 25]}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 5 } },
        }}
        isRowSelectable={(params: GridRowParams<Row>) =>
          isRowSelectable(params.row, dispenseMode, specType, allProducts)
        }
        getRowClassName={(params: GridRowParams<Row>) =>
          !isRowSelectable(params.row, dispenseMode, specType, allProducts)
            ? 'dispense-product-row--disabled'
            : ''
        }
        sx={gridSx}
      />
    </div>
  );

  const handleRemove = (id: any) => {
    const index = fields.findIndex((field) => field.id === id);
    if (index !== -1) {
      remove(index);
    }
  };

  const contactRows = groupedByType[ProductType.CONTACT_LENSES] ?? [];

  const basketUnits = (linesWatched ?? []).reduce(
    (sum, line) => sum + (line?.id ? lineQuantity(line) : 0),
    0,
  );
  const basketSubtotal = (linesWatched ?? []).reduce(
    (sum, line) =>
      sum + (line?.id ? (Number(line.price) || 0) * lineQuantity(line) : 0),
    0,
  );

  return (
    <Stack
      sx={{
        width: '100%',
      }}
      direction="column"
      p={1}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mt: 3, mb: 2 }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Products
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {basketUnits === 0
              ? 'No products added yet'
              : `${basketUnits} item${basketUnits === 1 ? '' : 's'} · ${formatGbp(basketSubtotal)}`}
          </Typography>
        </Box>
        {!disabled && (
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={handleAddFrame}
          >
            Add Product
          </Button>
        )}
      </Stack>

      {fields.length === 0 && (
        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 4,
            mb: 2,
            textAlign: 'center',
          }}
        >
          <Iconify
            icon="eva:shopping-cart-outline"
            width={32}
            sx={{ color: 'text.disabled', mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            The basket is empty. Use <strong>Add Product</strong> to pick
            frames, lenses or other items from inventory.
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {fields.map((field, index) => {
          const rowData = linesWatched?.[index] as
            Record<string, unknown> | undefined;
          const needsLot = isBatchAwareProduct(rowData);
          const pid = rowData?.id ? String(rowData.id) : '';
          const lots = pid ? (lotsByProductId[pid] ?? []) : [];
          const selectedLotId = rowData?.goodsInId
            ? String(rowData.goodsInId)
            : '';
          const selectedLot = selectedLotId
            ? lots.find((lot) => String(lot.id) === selectedLotId)
            : undefined;
          const selectedLotRemaining =
            selectedLot != null
              ? Number(selectedLot.qtyRemaining ?? 0)
              : undefined;
          const packSizeValue = Math.max(
            1,
            Number(rowData?.packSize ?? 1) || 1,
          );
          const quantityIssuedValue = Math.max(
            1,
            Number(rowData?.quantityIssued ?? rowData?.quantity ?? 1) || 1,
          );
          const effectiveUnits = packSizeValue * quantityIssuedValue;
          const lineQty = lineQuantity(rowData);
          const unitPrice = Number(rowData?.price) || 0;
          const stockLeft = pid ? stockByProduct.get(pid) : undefined;

          return (
            <Box
              key={field.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                mb: 2,
                backgroundColor: 'background.paper',
                boxShadow: 1,
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                spacing={2}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" noWrap>
                    {rowData?.name
                      ? String(rowData.name)
                      : 'No product selected'}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 0.5, flexWrap: 'wrap' }}
                  >
                    {!!rowData?.type && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={String(rowData.type)}
                      />
                    )}
                    {!!rowData?.category && (
                      <Typography variant="caption" color="text.secondary">
                        {String(rowData.category).replace(/_/g, ' ')}
                      </Typography>
                    )}
                    {stockLeft != null && !disabled && (
                      <Typography
                        variant="caption"
                        color={
                          stockLeft < lineQty ? 'error.main' : 'text.secondary'
                        }
                      >
                        · {stockLeft} in stock
                      </Typography>
                    )}
                  </Stack>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ minWidth: 96 }}
                >
                  {formatGbp(unitPrice)} each
                </Typography>

                {needsLot ? (
                  <Chip label={`× ${lineQty}`} sx={{ minWidth: 64 }} />
                ) : (
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      alignSelf: { xs: 'flex-start', md: 'center' },
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label="Decrease quantity"
                      disabled={disabled || lineQty <= 1}
                      onClick={() => setLineQuantity(index, lineQty - 1)}
                    >
                      <Iconify icon="eva:minus-fill" />
                    </IconButton>
                    <Typography
                      variant="subtitle2"
                      sx={{ minWidth: 32, textAlign: 'center' }}
                    >
                      {lineQty}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Increase quantity"
                      disabled={disabled}
                      onClick={() => setLineQuantity(index, lineQty + 1)}
                    >
                      <Iconify icon="eva:plus-fill" />
                    </IconButton>
                  </Stack>
                )}

                <Typography
                  variant="subtitle1"
                  sx={{ minWidth: 96, textAlign: { xs: 'left', md: 'right' } }}
                >
                  {formatGbp(unitPrice * lineQty)}
                </Typography>

                {!disabled && (
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Change product">
                      <IconButton onClick={() => openPicker(index)}>
                        <Iconify icon="eva:swap-fill" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove from basket">
                      <IconButton
                        color="error"
                        onClick={() => handleRemove(field.id)}
                      >
                        <Iconify icon="eva:trash-2-outline" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Stack>

              {needsLot && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    mt: 2,
                    alignItems: 'flex-start',

                    p: 2,

                    borderRadius: 2,

                    backgroundColor: '#fafafa',

                    border: '1px dashed',

                    borderColor: 'divider',
                  }}
                >
                  {' '}
                  <Typography
                    variant="subtitle2"
                    sx={{
                      width: '100%',
                      fontWeight: 600,
                      color: 'text.secondary',
                      mb: 1,
                    }}
                  >
                    Inventory Details
                  </Typography>
                  <Controller
                    name={`framesAndLenses.${index}.goodsInId`}
                    control={control}
                    defaultValue=""
                    render={({ field: lotField }) => (
                      <TextField
                        {...lotField}
                        select
                        label="Received lot"
                        sx={{ minWidth: 220 }}
                        disabled={disabled || !lots.length}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={lotField.value ?? ''}
                        onChange={(e) => {
                          const value = e.target.value ?? '';
                          lotField.onChange(value);
                        }}
                        helperText={
                          lots.length
                            ? 'From goods receipt (PO)'
                            : 'No received lots available. Receive stock first.'
                        }
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {lots.map((lot) => (
                          <MenuItem key={lot.id} value={lot.id}>
                            {(lot.batchNumber || 'Lot').toString()} — rem{' '}
                            {lot.qtyRemaining}
                            {lot.expiryDate
                              ? ` — exp ${new Date(
                                  lot.expiryDate,
                                ).toLocaleDateString()}`
                              : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  <Controller
                    name={`framesAndLenses.${index}.packSize`}
                    control={control}
                    defaultValue={1}
                    rules={{ min: { value: 1, message: 'Min 1' } }}
                    render={({ field: psField, fieldState }) => (
                      <TextField
                        {...psField}
                        type="number"
                        label="Pack size"
                        size="small"
                        sx={{ minWidth: 140 }}
                        disabled={disabled}
                        value={psField.value ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            psField.onChange('');
                            return;
                          }
                          const parsed = Math.floor(Number(raw));
                          if (!Number.isNaN(parsed)) {
                            psField.onChange(parsed);
                          }
                        }}
                        onBlur={() => {
                          psField.onBlur();
                          psField.onChange(
                            Math.max(1, Math.floor(Number(psField.value) || 1)),
                          );
                        }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                  <Controller
                    name={`framesAndLenses.${index}.quantityIssued`}
                    control={control}
                    defaultValue={1}
                    rules={{
                      min: { value: 1, message: 'Min 1' },
                    }}
                    render={({ field: qField, fieldState }) => (
                      <TextField
                        {...qField}
                        type="number"
                        label="Qty issued"
                        size="small"
                        sx={{ minWidth: 140 }}
                        disabled={disabled}
                        value={qField.value ?? ''}
                        onChange={(e) => {
                          const rawText = e.target.value;
                          if (rawText === '') {
                            qField.onChange('');
                            return;
                          }
                          const raw = Math.floor(Number(rawText));
                          if (Number.isNaN(raw)) return;
                          const maxIssuedByLot =
                            selectedLotRemaining != null
                              ? Math.max(
                                  1,
                                  Math.floor(
                                    selectedLotRemaining / packSizeValue,
                                  ) || 1,
                                )
                              : undefined;
                          const constrained =
                            maxIssuedByLot != null
                              ? Math.min(raw, maxIssuedByLot)
                              : raw;
                          qField.onChange(constrained);
                        }}
                        onBlur={() => {
                          qField.onBlur();
                          const raw = Math.max(
                            1,
                            Math.floor(Number(qField.value) || 1),
                          );
                          const maxIssuedByLot =
                            selectedLotRemaining != null
                              ? Math.max(
                                  1,
                                  Math.floor(
                                    selectedLotRemaining / packSizeValue,
                                  ) || 1,
                                )
                              : undefined;
                          qField.onChange(
                            maxIssuedByLot != null
                              ? Math.min(raw, maxIssuedByLot)
                              : raw,
                          );
                        }}
                        error={
                          !!fieldState.error ||
                          (!!selectedLotId &&
                            selectedLotRemaining != null &&
                            effectiveUnits > selectedLotRemaining)
                        }
                        helperText={
                          fieldState.error?.message ||
                          (selectedLotId && selectedLotRemaining != null
                            ? `Lot remaining: ${selectedLotRemaining} units`
                            : undefined)
                        }
                      />
                    )}
                  />
                  {needsLot && (
                    <TextField
                      label="Total units"
                      size="small"
                      sx={{
                        minWidth: 150,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#f5f5f5',
                          fontWeight: 600,
                        },
                      }}
                      disabled
                      value={effectiveUnits}
                      helperText="Pack size × qty issued"
                    />
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <Dialog
        fullWidth
        maxWidth="xl"
        open={formModal}
        onClose={handleCloseModal}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            p: 3,
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              textAlign: 'center',
              fontWeight: 600,
            }}
          >
            {isReplacing ? 'Change product' : 'Add products'}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, textAlign: 'center' }}
          >
            {isReplacing
              ? 'Pick the product to put on this line.'
              : 'Click a product (or its Add button) to put it in the basket. Adding the same product again increases its quantity.'}{' '}
            Unavailable items are disabled.
          </Typography>

          {dispenseMode === 'dispense_frames' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Dispense Frames:</strong> Contact lens products are shown
              but cannot be selected. All other types are available.
            </Alert>
          )}
          {dispenseMode === 'dispense_contact_lenses' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Dispense Contact Lenses:</strong> only contact lens
              products can be selected.
            </Alert>
          )}
          {dispenseMode === DISPENSE_OTHER_PRODUCTS && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Other products:</strong> All product types can be selected
              (Accessories, Sundries, Solution, Frames, Lenses, etc.).
            </Alert>
          )}
          {!dispenseMode && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Select <strong>Frames or Contact Lenses</strong> on the form so we
              can enforce the correct product types.
            </Alert>
          )}
          {specType &&
            SPEC_TYPE_TO_LENS_CATEGORY[specType] &&
            allProducts.some((r) => r.type === ProductType.LENSES) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Spec type ({specType}):</strong> spectacle lenses are
                limited to category &quot;{SPEC_TYPE_TO_LENS_CATEGORY[specType]}
                &quot; when your catalog includes matching products. Other
                product types are unaffected.
              </Alert>
            )}

          <TextField
            fullWidth
            size="small"
            placeholder="Search products..."
            value={filterSearch}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setFilterSearch(event.target.value)
            }
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify
                    icon="eva:search-fill"
                    sx={{ color: 'text.disabled' }}
                  />
                </InputAdornment>
              ),
            }}
          />

          {DISPENSE_PRODUCT_TYPE_ORDER.map((pt) => {
            const rows = groupedByType[pt] ?? [];
            const sectionTitle = PRODUCT_TYPE_LABELS[pt] ?? pt;
            return (
              <Accordion
                key={pt}
                expanded={expandedType === pt}
                onChange={handleAccordionChange(pt)}
                disableGutters
                sx={{
                  '&:before': { display: 'none' },

                  border: '1px solid',

                  borderColor: 'divider',

                  borderRadius: 2,

                  mb: 1,

                  boxShadow: 0,

                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: '#f8f9fa',
                    minHeight: 60,
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                    },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {sectionTitle}{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                    >
                      ({rows.length} shown)
                    </Typography>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {pt === ProductType.CONTACT_LENSES ? (
                    contactRows.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No contact lens products found.
                      </Typography>
                    ) : (
                      <Stack spacing={2}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Checkbox
                            {...differentRLCheckboxProps}
                            checked={differentRL}
                            onChange={() => setDifferentRL(!differentRL)}
                          />
                          <Typography variant="body2">
                            Different R&amp;L
                          </Typography>
                        </Box>
                        <Typography variant="subtitle2">Right</Typography>
                        {renderTypeGrid(contactRows, `${pt}-r`)}
                        {differentRL && (
                          <>
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                              Left
                            </Typography>
                            {renderTypeGrid(contactRows, `${pt}-l`)}
                          </>
                        )}
                      </Stack>
                    )
                  ) : rows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No products found for this category.
                    </Typography>
                  ) : (
                    renderTypeGrid(rows, pt)
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}

          {(groupedByType.other?.length ?? 0) > 0 && (
            <Accordion
              expanded={expandedType === 'other'}
              onChange={handleAccordionChange('other')}
              disableGutters
              sx={{ '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {PRODUCT_TYPE_LABELS.other}{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    ({groupedByType.other.length} shown)
                  </Typography>
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {renderTypeGrid(groupedByType.other, 'other')}
              </AccordionDetails>
            </Accordion>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 3,
              pt: 2,

              borderTop: '1px solid',

              borderColor: 'divider',

              gap: 1,
            }}
          >
            {!isReplacing && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mr: 'auto', alignSelf: 'center' }}
              >
                Basket: {basketUnits} item{basketUnits === 1 ? '' : 's'} ·{' '}
                {formatGbp(basketSubtotal)}
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleCloseModal}
              sx={{ minWidth: 120 }}
            >
              {isReplacing ? 'Cancel' : 'Done'}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Stack>
  );
}
